<script setup>
import { useNotifier } from '@daysmart/daysmart-vue-components/composables'

const props = defineProps({
  eligibility: {
    type: Object,
  },
  event: {
    type: Object,
  },
})

const emit = defineEmits(['update'])
const api = useApi()
const { t } = useI18n()
const notify = useNotifier()
const selectedCustomer = useSelectedCustomer()
const { calendarSettings } = useSessionData()

const showWaitlistAlert = toRef(() => props.event?.customer_on_waitlist)

const isDailyWaitlistMaxed = toRef(() => props.eligibility?.global_restrictions?.some(restriction => restriction.code === 'maximum_daily_waitlists'))

const hasReachedRegistrationLimit = props.eligibility?.global_restrictions?.some(restriction => restriction.code === 'maximum_daily_registrations')
  || props.eligibility?.global_restrictions?.some(restriction => restriction.code === 'maximum_weekly_registrations')

const allowWaitlist = computed(() => {
  return props.event?.active_registration_status?.suggested_action === 'join_waitlist' && !isDailyWaitlistMaxed.value && !hasReachedRegistrationLimit
})

async function joinWaitlist(finishProcessing) {
  try {
    await api.joinWaitlist(props.event.id, {
      data: {
        customer: selectedCustomer.value,
      },
    })

    notify.emit({
      type: 'success',
      title: t('Success'),
      content: t('Joined Waitlist'),
    })

    emit('update')
  }
  catch (error) {
    if (error.response?.data?.code === 'event_waitlist_not_open') {
      notify.emit({
        type: 'danger',
        content: t('This class is full'),
      })
      emit('update')
    }
    else {
      throw error
    }
  }

  finishProcessing()
}

async function leaveWaitlist(finishProcessing) {
  await api.leaveWaitlist(props.event.id, {
    data: {
      customer: selectedCustomer.value,
    },
  })

  finishProcessing()

  notify.emit({
    type: 'success',
    title: t('Success'),
    content: t('Left Waitlist'),
  })

  emit('update')
}
</script>

<template>
  <template v-if="showWaitlistAlert">
    <div class="flex grow flex-col items-center justify-between flex-gap-y-4 whitespace-pre-wrap">
      <DSAlert
        type="info"
        no-close
        class="w-100%"
      >
        {{ calendarSettings.show_waitlist_positions ? `${t('You’re #{waitlistPosition} on the waitlist.', { waitlistPosition: props.event?.waitlist_position })}` : t('You’re on the waitlist.') }}
      </DSAlert>
      <DSAsyncButton
        class="btn btn-md w-full truncate uppercase btn-fill-tertiary"
        @submit="leaveWaitlist"
      >
        <span>{{ t('Leave Waitlist') }}</span>
      </DSAsyncButton>
    </div>
  </template>
  <template v-else-if="isDailyWaitlistMaxed">
    <DSAlert
      type="danger"
      no-close
      class="w-100%"
    >
      {{ t('You cannot join the waitlist for this class because you have already joined the maximum number of waitlists for this day') }}.
    </DSAlert>
  </template>
  <template v-else>
    <div class="flex grow flex-col items-center flex-gap-y-2">
      <!-- class is full -->
      <DSAlert
        type="danger"
        no-close
        class="w-100%"
      >
        {{ t('This class is full') }}.
      </DSAlert>
      <ScheduleBookingModalRegistrationLimitReachedStep
        v-if="hasReachedRegistrationLimit"
        :eligibility="props.eligibility"
      />
    </div>
    <!-- Join Waitlist Button -> if allowed -->
    <DSAsyncButton
      v-if="allowWaitlist"
      class="btn btn-md w-full truncate uppercase btn-fill-primary"
      @submit="joinWaitlist"
    >
      <span>{{ t('Join Waitlist') }}</span>
    </DSAsyncButton>
  </template>
</template>

<i18n>
{
  "en-us": {
    "This class is full": "This class is full",
    "You’re #{waitlistPosition} on the waitlist.": "You’re #{waitlistPosition} on the waitlist.",
    "You’re on the waitlist.": "You’re on the waitlist.",
    "Join Waitlist": "Join Waitlist",
    "Leave Waitlist": "Leave Waitlist",
    "Success": "Success",
    "Joined Waitlist": "Joined Waitlist",
    "Left Waitlist": "Left Waitlist",
    "You cannot join this class because you are attending the maximum number of classes for this day": "You cannot join this class because you are attending the maximum number of classes for this day",
    "You cannot join the waitlist for this class because you have already joined the maximum number of waitlists for this day": "You cannot join the waitlist for this class because you have already joined the maximum number of waitlists for this day"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "This class is full": "Dieser Kurs ist voll",
  "You’re #{waitlistPosition} on the waitlist.": "Sie sind auf der Warteliste an Position {waitlistPosition}.",
  "You’re on the waitlist.": "Du stehst auf der Warteliste.",
  "Join Waitlist": "Warteliste beitreten",
  "Leave Waitlist": "Warteliste verlassen",
  "Success": "Erfolg",
  "Joined Waitlist": "Warteliste beigetreten",
  "Left Waitlist": "Warteliste verlassen",
  "You cannot join this class because you are attending the maximum number of classes for this day": "Du kannst an diesem Kurs leider nicht teilnehmen, da du die maximale Anzahl von Buchungen für einen Tag erreicht hast.",
  "You cannot join the waitlist for this class because you have already joined the maximum number of waitlists for this day": "Du kannst dich nicht auf die Wartelist für diesen Kurs setzen lassen, da du die maximale Anzahl an Wartelisten für einen Tag erreicht hast."
}
</i18n>

<i18n locale="fr" lang="json">
{
  "This class is full": "Ce cours est complet",
  "You’re #{waitlistPosition} on the waitlist.": "Vous êtes n° {waitlistPosition} sur la liste d'attente",
  "You’re on the waitlist.": "Vous êtes sur la liste d'attente",
  "Join Waitlist": "S'inscrire sur la liste d'attente",
  "Leave Waitlist": "Quitter la liste d'attente",
  "Success": "Succès",
  "Joined Waitlist": "Inscrit à la liste d'attente",
  "Left Waitlist": "Liste d'attente quittée",
  "You cannot join this class because you are attending the maximum number of classes for this day": "Vous ne pouvez pas rejoindre ce cours, car vous assistez déjà au nombre maximum de cour pour ce jour",
  "You cannot join the waitlist for this class because you have already joined the maximum number of waitlists for this day": "Vous ne pouvez pas vous inscrire à la liste d'attente de ce cours, car vous êtes déjà inscrit au nombre maximum de listes d'attente pour ce jour"
}
</i18n>

<i18n locale="es" lang="json">
{
  "This class is full": "Esta clase está completa",
  "You’re #{waitlistPosition} on the waitlist.": "Está en la {waitlistPosition} posición de la lista de espera.",
  "You’re on the waitlist.": "Está en la lista de espera.",
  "Join Waitlist": "Entrar en la lista de espera",
  "Leave Waitlist": "Salir de la lista de espera",
  "Success": "Correcto",
  "Joined Waitlist": "Ha entrado en la lista de espera",
  "Left Waitlist": "Ha salido de la lista de espera",
  "You cannot join this class because you are attending the maximum number of classes for this day": "No puede entrar en esta clase porque ya asiste al número máximo de clases por hoy",
  "You cannot join the waitlist for this class because you have already joined the maximum number of waitlists for this day": "No puede entrar en la lista de espera de esta clase porque ya ha entrado en el número máximo de listas de espera por hoy"
}
</i18n>
